import React, { useState } from "react";
import { Col, Row } from "reactstrap";
import { Link } from "react-router-dom";
import { useQuery } from "react-query";
import JobApplicationModal from "../../../components/JobApplicationModal";
import { getSimilarVacancies } from "../../../services/vacancyService";
import { translateJobType } from "../../../utils/jobTranslations";
import { useLanguage } from "../../../context/LanguageContext";

export const formatSalary = (min, max) => {
  if (min >= 1000 || max >= 1000) {
    return `$${(min / 1000).toFixed(0)}k - $${(max / 1000).toFixed(0)}k / year`;
  }
  return `$${min} - $${max} / month`;
};

const JobVacancyPost = ({ jobId }) => {
  const [modalOpen, setModalOpen] = useState(false);
  const toggleModal = () => setModalOpen(!modalOpen);
  const { language } = useLanguage();

  const {
    data: similarJobs = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["similarJobs", jobId],
    queryFn: () => getSimilarVacancies(jobId),
    select: (data) => data.content,
    enabled: !!jobId, // Only fetch when jobId is available
    staleTime: 5 * 60 * 1000, // 5 minutes cache
  });

  if (isLoading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">
            {language === "pt" ? "Carregando..." : "Loading..."}
          </span>
        </div>
        <p className="mt-2">
          {language === "pt"
            ? "Carregando vagas similares..."
            : "Loading similar jobs..."}
        </p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-4 text-danger">
        <i className="uil uil-exclamation-triangle fs-4"></i>
        <p className="mt-2">{error.message || "Failed to load similar jobs"}</p>
      </div>
    );
  }

  if (similarJobs.length === 0) {
    return (
      <div className="text-center py-4">
        <p>
          {language === "pt"
            ? "Nenhuma vaga similar encontrada"
            : "No similar jobs found"}
        </p>
        <Link to="/joblist" className="btn btn-soft-primary mt-2">
          {language === "pt" ? "Ver todas as vagas" : "View all jobs"}
        </Link>
      </div>
    );
  }

  return (
    <React.Fragment>
      <h5 className="fs-18 my-3">
        {language === "pt" ? "Vagas Similares" : "Similar Vacancies"}
      </h5>

      {similarJobs.map((job, key) => (
        <div key={key} className="job-box card mt-4">
          <div className="p-4">
            <Row>
              <Col lg={10}>
                <div className="mt-3 mt-lg-0">
                  <h5 className="fs-17 mb-1">
                    <Link to={`/jobdetails/${job.id}`} className="text-dark">
                      {job.title}
                    </Link>{" "}
                  </h5>
                  <ul className="list-inline mb-0">
                    <li className="list-inline-item">
                      <small className="text-muted fw-normal">
                        {job.yearsOfExperience || 0}
                        {language === "pt"
                          ? " anos de experiência"
                          : " years experience"}
                      </small>
                    </li>
                    <li className="list-inline-item">
                      <p className="text-muted fs-14 mb-0">
                        <i className="mdi mdi-map-marker"></i>
                        {job.city || job.country || "Remote"}
                      </p>
                    </li>
                  </ul>
                  <div className="mt-2">
                    <span
                      className={`badge bg-${
                        job.remoteAllowed ? "success" : "primary"
                      }-subtle text-${
                        job.remoteAllowed ? "success" : "primary"
                      } fs-13 mt-1 mx-1`}
                    >
                      {translateJobType(job.type, language)}
                    </span>
                    {job.jobCategory && (
                      <span className="badge bg-info-subtle text-info fs-13 mt-1">
                        {job.jobCategory.name}
                      </span>
                    )}
                  </div>
                </div>
              </Col>
            </Row>
            <div className="favorite-icon">
              <Link to="#">
                <i className="uil uil-heart-alt fs-18"></i>
              </Link>
            </div>
          </div>
          <div className="p-3 bg-light">
            <div className="row justify-content-between">
              <Col md={3}>
                <div className="text-md-end">
                  <Link to={`/jobdetails/${job.id}`} className="primary-link">
                    {language === "pt" ? "Ver Detalhes" : "View Details"}{" "}
                    <i className="mdi mdi-chevron-double-right"></i>
                  </Link>
                </div>
              </Col>
            </div>
          </div>
        </div>
      ))}

      <div className="text-center mt-4">
        <Link to="/joblist" className="primary-link form-text">
          {language === "pt" ? "Ver Todas Vagas" : "View All Jobs"}{" "}
          <i className="mdi mdi-arrow-right"></i>
        </Link>
      </div>

      <JobApplicationModal isOpen={modalOpen} toggle={toggleModal} />
    </React.Fragment>
  );
};

export default JobVacancyPost;
